// Team Members API Layer - Low-level HTTP calls
import { getToken } from "../../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../../config/api";

export interface TeamMemberDTO {
  id: number;
  userName: string;
  email: string;
  phone_number: string | null;
  role: string;
  profile_picture: string | null;
}

interface TeamMembersResponseDTO {
  success: boolean;
  data: TeamMemberDTO[];
  count: number;
}

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const createHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

const makeRequest = async (endpoint: string, options: any = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...createHeaders(),
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    let errorData = null;
    try {
      errorData = await response.json();
    } catch {
      // If response is not JSON, use status text
    }

    throw new APIError(
      errorData?.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json();
};

export const teamMembersAPI = {
  /**
   * Get all team members (excluding admin)
   */
  getTeamMembers: async (): Promise<TeamMemberDTO[]> => {
    const response: TeamMembersResponseDTO = await makeRequest(
      "/api/users/team-members",
      {
        method: "GET",
      }
    );

    if (!response.success) {
      throw new APIError("Failed to fetch team members", 500, response);
    }

    return response.data;
  },
};

